import { yupResolver } from '@hookform/resolvers/yup';
import { MAX_TAG_NAME_LENGTH, getTagErrorMessage, normalizeTagName } from '@vizit/api';
import { FormDialog, FormTextField } from '@vizit/forms';
import { Edit03Icon, PlusIcon } from '@vizit/icons';
import { DialogHeaderStatus, DialogHeaderVariant } from '@vizit/molecules';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

type TagNameFormSchema = {
  name: string;
};

// Built on first render, not at import: modules that mock `@vizit/api` and import this package must still load.
const createFormSchema = () =>
  yup.object().shape({
    name: yup
      .string()
      .required('Tag name is required')
      // The API trims, collapses whitespace and lowercases before it applies its rules, so validate that form.
      .test('not-blank', 'Tag name is required', (value) => normalizeTagName(value).length > 0)
      .test(
        'max-length',
        `Please limit tag name to ${MAX_TAG_NAME_LENGTH} characters`,
        (value) => normalizeTagName(value).length <= MAX_TAG_NAME_LENGTH,
      ),
  });

interface TagNameDialogProps {
  /**
   * The tag being renamed. When omitted the dialog creates a new tag.
   *
   * @optional
   * @type {{ id: string; name: string }}
   */
  tag?: { id: string; name: string };
  /**
   * The name the field starts with when creating, for example the search query that matched nothing.
   *
   * @optional
   * @type {string}
   */
  defaultName?: string;
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Callback to notify when the dialog open state changes
   * @param open - Whether the dialog should be open
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Saves the normalized name. Reject to keep the dialog open and show the error inline.
   *
   * @required
   * @type {(name: string) => Promise<void>}
   */
  onSubmit: (name: string) => Promise<void>;
}

/**
 * Dialog to create a tag or rename an existing one. The typed text is kept when the request fails
 * and the API error is shown under the field.
 *
 * @param props - The props for the TagNameDialog component.
 */
function TagNameDialog(props: TagNameDialogProps) {
  const { tag, defaultName = '', isOpen, onOpenChange, onSubmit } = props;
  const initialName = tag?.name ?? defaultName;
  const formSchema = useMemo(createFormSchema, []);

  const formMethods = useForm<TagNameFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: { name: initialName },
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, reset, setError, clearErrors } = formMethods;

  const handleOnResetForm = () => {
    clearErrors();
    reset({ name: initialName });
  };

  const handleFormSubmit = async ({ name }: TagNameFormSchema) => {
    const normalized = normalizeTagName(name);

    if (tag && normalized === tag.name) {
      onOpenChange?.(false);
      return;
    }

    try {
      await onSubmit(normalized);
      onOpenChange?.(false);
    } catch (error) {
      setError('name', { message: getTagErrorMessage(error) });
    }
  };

  return (
    <FormDialog
      formMethods={formMethods}
      formName="tag-name"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit(handleFormSubmit)}
      onResetForm={handleOnResetForm}
      headerVariant={DialogHeaderVariant.Icon}
      headerStatus={DialogHeaderStatus.Info}
      headerIcon={tag ? Edit03Icon : PlusIcon}
    >
      <FormDialog.Title>{tag ? 'Edit tag' : 'New tag'}</FormDialog.Title>
      <FormDialog.FormFields>
        <FormTextField
          control={control}
          name="name"
          label="Name"
          placeholder="e.g. holiday-2025"
          autoFocus
          isRequired
        />
      </FormDialog.FormFields>
      <FormDialog.SubmitButton>{tag ? 'Save' : 'Create tag'}</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { TagNameDialog };
export type { TagNameDialogProps, TagNameFormSchema };
